export type ProductCatalogSpecRow = {
  id?: number | string;
  family?: string;
  source?: string;
  schemaVersion?: string;
  specs?: Record<string, unknown> | null;
};

type HydrateEditFormInput = {
  product: { family?: string | null } | null | undefined;
  existingSpecs?: Record<string, unknown> | null;
  technicalSpecRows?: ProductCatalogSpecRow[] | null;
};

function hasValue(value: unknown) {
  if (value === null || value === undefined || value === "") return false;
  if (typeof value === "string" && !value.trim()) return false;
  if (Array.isArray(value)) return value.some(hasValue);
  if (typeof value === "object") return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
}

function rawSpecEntries(specs: Record<string, unknown>) {
  return (specs.rawProperties && typeof specs.rawProperties === "object" ? specs.rawProperties : {}) as Record<string, unknown>;
}

function normalizeSpecKey(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function findRawSpec(raw: Record<string, unknown>, wantedKeys: string[]) {
  const wanted = wantedKeys.map(normalizeSpecKey);
  const entries = Object.entries(raw).map(([key, value]) => ({ key: normalizeSpecKey(key), value }));
  const exact = entries.find((entry) => wanted.includes(entry.key) && hasValue(entry.value));
  if (exact) return exact.value;
  return entries.find((entry) => wanted.some((key) => entry.key.includes(key)) && hasValue(entry.value))?.value;
}

function findSpec(specs: Record<string, unknown>, directKeys: string[], rawKeys: string[] = []) {
  for (const key of directKeys) {
    if (hasValue(specs[key])) return specs[key];
  }
  const raw = rawSpecEntries(specs);
  return findRawSpec(raw, [...directKeys, ...rawKeys]);
}

function isCanonicalSpecRow(row: ProductCatalogSpecRow | null | undefined) {
  return row?.schemaVersion === "canonical-v1";
}

function operationalSpecsForEdit(rows: ProductCatalogSpecRow[] = []) {
  const canonical = rows.find(isCanonicalSpecRow);
  return canonical || rows.find((item) => item?.source === "manual") || rows[0] || null;
}

function writeIfEmpty(target: Record<string, unknown>, key: string, value: unknown) {
  if (!hasValue(value) || hasValue(target[key])) return;
  target[key] = value;
}

function positiveNumeric(value: unknown) {
  const number = typeof value === "number" ? value : Number(String(value ?? "").trim().replace(",", "."));
  return Number.isFinite(number) && number > 0 ? number : null;
}

function firstPositiveSpecValue(specs: Record<string, unknown>, directKeys: string[], rawKeys: string[] = directKeys) {
  for (const key of directKeys) {
    const value = positiveNumeric(specs[key]);
    if (value !== null) return value;
  }
  const raw = rawSpecEntries(specs);
  for (const key of rawKeys) {
    const value = positiveNumeric(raw[key]);
    if (value !== null) return value;
  }
  const found = findSpec(specs, [], rawKeys);
  return positiveNumeric(found);
}

function specsWithBatteryDisplayRepair(row: ProductCatalogSpecRow | null, rows: ProductCatalogSpecRow[] = []) {
  const specs = row?.specs && typeof row.specs === "object" ? { ...row.specs } : {};
  if (!row || !isCanonicalSpecRow(row) || row.family !== "baterias") return specs;

  const siblingSpecs = rows
    .filter((item) => item?.id !== row.id)
    .map((item) => item?.specs && typeof item.specs === "object" ? item.specs : {})
    .filter((item) => Object.keys(item).length > 0);

  if ((positiveNumeric(specs.nominalCapacityWh) ?? 0) <= 0) {
    const nominal = firstPositiveSpecValue(specs, ["nominalCapacityWh"], ["nominal_energy", "nominal_capacity", "capacity"])
      ?? siblingSpecs.map((item) => firstPositiveSpecValue(item, ["nominalCapacityWh", "capacity"], ["nominal_energy", "nominal_capacity", "capacity"])).find((value) => value !== null);
    if (nominal !== null && nominal !== undefined) specs.nominalCapacityWh = nominal;
  }
  if ((positiveNumeric(specs.usableCapacityWh) ?? 0) <= 0) {
    const usable = firstPositiveSpecValue(specs, ["usableCapacityWh"], ["storage_capacity_usable", "usable_capacity", "useful_capacity"])
      ?? siblingSpecs.map((item) => firstPositiveSpecValue(item, ["usableCapacityWh", "capacity"], ["storage_capacity_usable", "usable_capacity", "useful_capacity", "capacity"])).find((value) => value !== null);
    if (usable !== null && usable !== undefined) specs.usableCapacityWh = usable;
  }
  for (const key of ["cyclesLifetime", "productWarrantyYears", "compatibility"]) {
    if (!hasValue(specs[key])) {
      const fromSibling = siblingSpecs.map((item) => item[key]).find(hasValue);
      if (fromSibling !== undefined) specs[key] = fromSibling;
    }
  }
  return specs;
}

function hydratePanelSpecs(target: Record<string, unknown>, source: Record<string, unknown>) {
  const directMappings: Array<[string, string[], string[]]> = [
    ["powerWp", ["powerWp"], ["modules_power_output", "power_output", "potencia", "pmax", "power_wp"]],
    ["vocV", ["vocV", "voc"], ["open_circuit_voltage", "voc", "voc_v"]],
    ["iscA", ["iscA", "isc"], ["short_circuit_current", "isc", "isc_a"]],
    ["vmpV", ["vmpV", "vmp"], ["vmp", "vmpp", "voltageAtMaxPower", "voltage_at_max_power", "maximumPowerVoltage", "maximum_power_voltage", "ratedVoltageVmpp", "rated_voltage_vmpp", "voltage_at_pmax", "max_power_voltage", "v_mpp", "vmp_v", "voltage", "voltaje", "voltaje_operacion", "operatingVoltage", "operationVoltage"]],
    ["impA", ["impA", "imp"], ["imp", "impp", "currentAtMaxPower", "current_at_max_power", "maximumPowerCurrent", "maximum_power_current", "ratedCurrentImpp", "rated_current_impp", "current_at_pmax", "max_power_current", "i_mpp", "imp_a", "current", "corriente", "corriente_operacion", "operatingCurrent", "operationCurrent"]],
    ["efficiencyPct", ["efficiencyPct"], ["efficiency", "eficiencia", "module_efficiency", "rendimiento", "eff", "efficiency_pct"]],
    ["heightMm", ["heightMm"], ["height", "alto", "altura", "frame_thickness", "thickness", "espesor", "module_thickness", "depth_mm"]],
    ["widthMm", ["widthMm"], ["width", "ancho", "anchura", "module_width", "dimension_width", "width_mm"]],
    ["depthMm", ["depthMm"], ["length", "depth", "largo", "longitud", "module_depth", "dimension_height", "module_height", "height_mm"]],
    ["weightKg", ["weightKg"], ["weight", "peso", "module_weight", "weight_kg", "kg"]],
    ["bifacial", ["bifacial"], ["bifacial", "bifaciality"]],
    ["productWarrantyYears", ["productWarrantyYears"], ["product_warranty", "warranty", "warranty_years", "garantia_producto"]],
    ["productionWarrantyYears", ["productionWarrantyYears", "performanceWarrantyYears"], ["production_warranty", "performance_warranty", "power_warranty", "linear_power_warranty", "garantia_produccion", "garantia_potencia"]],
    ["annualDegradationRateFirstYear", ["annualDegradationRateFirstYear"], ["annual_degradation_rate", "annual_degradation_first_year", "degradation_first_year", "degradacion_anual_1"]],
    ["annualDegradationRateSubsequentYears", ["annualDegradationRateSubsequentYears", "annualDegradationPct"], ["annual_degradation", "annual_degradation_rate_subsequent", "degradation_subsequent_years", "degradacion_anual", "degradacion_anual_resto"]],
    ["connector", ["connector", "connectorType"], ["connector_type", "connector", "tipo_conector"]],
    ["cellsCount", ["cellsCount", "cellCount"], ["cells", "cell_number", "cells_number", "number_of_cells", "celulas", "celdas"]],
    ["diodesCount", ["diodesCount", "diodeCount"], ["diodes", "diodes_number", "diodos", "bypass_diodes"]],
    ["moduleType", ["moduleType"], ["module_type", "module_material", "cell_technology_type", "cell_type", "tipo_modulo", "material", "technology", "tecnologia", "cell_technology", "tipo_celula", "frame_material"]],
  ];

  for (const [targetKey, directKeys, rawKeys] of directMappings) {
    writeIfEmpty(target, targetKey, findSpec(source, directKeys, rawKeys));
  }

  const positiveCable = findSpec(source, ["cablePositiveMm", "positiveCableLengthMm"], ["positive_cable_length", "cable_positive", "cable_positivo", "cable_length_positive"]);
  const negativeCable = findSpec(source, ["cableNegativeMm", "negativeCableLengthMm"], ["negative_cable_length", "cable_negative", "cable_negativo", "cable_length_negative"]);
  writeIfEmpty(target, "cablePositiveMm", positiveCable);
  writeIfEmpty(target, "cableNegativeMm", negativeCable);
  if (!hasValue(target.cablePositiveNegativeMm) && hasValue(positiveCable) && String(positiveCable) === String(negativeCable)) {
    target.cablePositiveNegativeMm = positiveCable;
  }
}

function hydrateBatterySpecs(target: Record<string, unknown>, source: Record<string, unknown>) {
  const directMappings: Array<[string, string[], string[]]> = [
    ["nominalCapacityWh", ["nominalCapacityWh", "capacity"], ["nominal_capacity_wh", "capacity_wh", "nominal_energy", "nominal_capacity", "energy_capacity", "capacity", "energia_nominal"]],
    ["usableCapacityWh", ["usableCapacityWh", "usableCapacity"], ["usable_capacity_wh", "usable_capacity", "useful_capacity", "useful_energy", "storage_capacity_usable"]],
    ["voltageV", ["voltageV", "voltage"], ["voltage", "tension", "voltage_v", "min_working_voltage_single_phase", "max_working_voltage_single_phase"]],
    ["chemistry", ["chemistry"], ["battery_type", "chemistry", "quimica"]],
    ["chargePowerW", ["chargePowerW"], ["charge_power_w", "charge_power", "charging_power", "max_power_load"]],
    ["dischargePowerW", ["dischargePowerW"], ["discharge_power_w", "discharge_power", "discharging_power", "max_power_discharge"]],
    ["heightMm", ["heightMm"], ["height", "alto"]],
    ["widthMm", ["widthMm"], ["width", "ancho"]],
    ["depthMm", ["depthMm"], ["length", "largo", "depth"]],
    ["weightKg", ["weightKg"], ["weight", "peso"]],
    ["productWarrantyYears", ["productWarrantyYears"], ["product_warranty", "warranty"]],
    ["protectionIp", ["protectionIp"], ["ip", "protection", "protection_degree", "protection_class"]],
    ["cyclesLifetime", ["cyclesLifetime", "cycles"], ["cycles", "cycle_life"]],
    ["compatibility", ["compatibility", "compatibilities"], ["compatibilities", "compatible_inverters", "compatibility"]],
  ];

  for (const [targetKey, directKeys, rawKeys] of directMappings) {
    writeIfEmpty(target, targetKey, findSpec(source, directKeys, rawKeys));
  }
}

export function hydrateEditFormFromCanonicalSpecs(input: HydrateEditFormInput) {
  const target = { ...(input.existingSpecs || {}) };
  const rows = input.technicalSpecRows || [];
  const row = operationalSpecsForEdit(rows);
  const source = specsWithBatteryDisplayRepair(row, rows);
  const family = input.product?.family || row?.family;

  if (!Object.keys(source).length) return target;
  if (family === "paneles") hydratePanelSpecs(target, source);
  if (family === "baterias") hydrateBatterySpecs(target, source);
  return target;
}


export function shouldShowWattwinAdminTools(input: { canRunWattwinAdmin: boolean; isStagingUi: boolean }) {
  return input.canRunWattwinAdmin && input.isStagingUi;
}

export function isRawCategoryIdentifier(value: unknown) {
  return typeof value === "string" && /^[a-f0-9]{24}$/i.test(value.trim());
}

export function categoryValueForEditDisplay(value: unknown) {
  return isRawCategoryIdentifier(value) ? "" : String(value ?? "");
}


type EditAssetStatusInput = {
  status?: string | null;
  assetType?: string | null;
  purpose?: string | null;
  viewUrl?: string | null;
};

function isCachedEditAsset(asset: EditAssetStatusInput) {
  return (asset.status === "cached" || asset.status === "external_url_ready") && !!asset.viewUrl;
}

export function editAssetStatusForDisplay(assets: EditAssetStatusInput[] = []) {
  const image = assets.find((asset) => isCachedEditAsset(asset) && asset.assetType === "image" && ["main_image", "gallery_image"].includes(String(asset.purpose || "")));
  const technicalSheets = assets.filter((asset) => isCachedEditAsset(asset) && asset.assetType !== "image" && asset.purpose === "technical_sheet");
  return {
    image: image ? "cached" as const : "missing" as const,
    technicalSheet: technicalSheets.length ? "cached" as const : "missing" as const,
    technicalSheetUrls: technicalSheets.map((asset) => String(asset.viewUrl)),
  };
}
